import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import type { WeeklyVisitItem } from '@/api/visits';
import type { SortMode, PriorityMode } from '@/components/SortMenu';
import { locationApi } from '@/api/location';
import {
  optimizeRoute,
  optimizeRouteWithPriorities,
  type OptimizeRouteMode,
} from '@/api/geo';
import {
  getVisitPriority,
  PRIORITY_CODES,
  type PriorityType,
} from '@/lib/priority';

// Cached Russian collator for fast string comparison
const ruCollator = new Intl.Collator('ru').compare;

export interface SortedVisitItem extends WeeklyVisitItem {
  priority: PriorityType;
  distanceKm?: number;
}

interface UseVisitsSortingOptions {
  visits: WeeklyVisitItem[] | undefined;
  sortMode: SortMode;
  priorityMode: PriorityMode;
  activeVisitTargetId?: number;
}

interface UseVisitsSortingResult {
  sortedVisits: SortedVisitItem[];
  pendingVisits: SortedVisitItem[];
  completedVisits: SortedVisitItem[];
  isCalculating: boolean;
  startLocation: { lat: number; lon: number } | null;
  error: string | null;
}

/**
 * Calculate priority for a visit item based on legacy logic
 *
 * Логика plannedDate и deadline:
 * - plannedDate: дата "активации" визита (до этой даты запись игнорируется)
 * - deadline: крайний срок (определяет красный цвет)
 *
 * Запись "активна" если plannedDate отсутствует ИЛИ plannedDate ≤ сегодня
 */
function calculatePriority(item: WeeklyVisitItem, activeVisitTargetId?: number): PriorityType {
  const isCancelled = item.visit?.status === 'CANCELLED';

  // Проверяем "активацию" по plannedDate
  // Запись активна если plannedDate отсутствует или plannedDate ≤ сегодня
  const today = new Date().toISOString().split('T')[0];
  const plannedDate = item.visit?.plannedDate;
  const isRecordActive = !plannedDate || plannedDate <= today;

  // Если запись не активна (plannedDate в будущем) — возвращаем regular (белый фон)
  if (!isRecordActive && !isCancelled) {
    return 'regular';
  }

  const isActive = activeVisitTargetId === item.visitTargetId;
  const isVisited = !isCancelled && item.visit?.status === 'VISITED';
  const isSkipped = !isCancelled && item.visit?.status === 'SKIPPED';
  const isUrgent = !isCancelled && item.visit?.priority === 'URGENT';

  // Urgent за 2 часа до запланированного времени в день дедлайна
  const isUrgentByTime = !isCancelled && item.deadlineStatus === 'today' && !!item.visit?.plannedTime && (() => {
    const [h, m] = item.visit!.plannedTime!.split(':').map(Number);
    const now = new Date();
    const visitTime = new Date();
    visitTime.setHours(h, m, 0, 0);
    const diffMs = visitTime.getTime() - now.getTime();
    return diffMs > 0 && diffMs <= 2 * 60 * 60 * 1000;
  })();

  // deadlineStatus теперь вычисляется из deadline на бэкенде
  const hasDeadline = !isCancelled && !!item.deadlineStatus;
  const hasOutdoorTask = false;

  // PENDING без deadline = зелёный
  const hasPendingVisit = !isCancelled
    && item.visit?.status === 'PENDING'
    && !hasDeadline;

  const priority = getVisitPriority({
    isActive,
    isUrgent: isUrgent || isUrgentByTime,
    hasDeadline,
    isPreferredDayToday: item.isPreferredDayToday,
    hasPendingVisit,
    isVisited,
    isSkipped,
    hasOutdoorTask,
  });

  return priority;
}

/**
 * Get priority group for sorting (lower = higher priority)
 * Used for grouping when usePriority is enabled
 */
function getPriorityGroup(priority: PriorityType): number {
  // Use PRIORITY_CODES for sorting - higher code = higher priority = lower group number
  return -PRIORITY_CODES[priority];
}

/**
 * Check if visit should be sorted (not completed or has pending revisit)
 */
function shouldSortVisit(item: SortedVisitItem): boolean {
  if (item.priority === 'been') {
    return false;
  }
  return true;
}

/**
 * Sort visits by name (А-Я)
 */
function sortByName(items: SortedVisitItem[]): SortedVisitItem[] {
  return [...items].sort((a, b) => ruCollator(a.client.name, b.client.name));
}

/**
 * Map SortMode to OptimizeRouteMode
 */
function getOptimizeMode(sortMode: SortMode): OptimizeRouteMode {
  switch (sortMode) {
    case 'fromMe':
      return 'fromMe';
    case 'fromLast':
      return 'fromLast';
    case 'shortest':
      return 'shortest';
    default:
      return 'fromLast';
  }
}

/**
 * Hook for sorting visits using backend route optimization
 */
export function useVisitsSorting({
  visits,
  sortMode,
  priorityMode,
  activeVisitTargetId,
}: UseVisitsSortingOptions): UseVisitsSortingResult {
  // For backward compatibility: 'priority' and 'priorityGps' both use priority grouping
  const usePriority = priorityMode !== 'none';
  const [isCalculating, setIsCalculating] = useState(false);
  const [startLocation, setStartLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortedOrder, setSortedOrder] = useState<number[] | null>(null);

  // Calculate priority for each visit
  const visitsWithPriority = useMemo(() => {
    if (!visits) return [];
    return visits.map((item) => ({
      ...item,
      priority: calculatePriority(item, activeVisitTargetId),
    }));
  }, [visits, activeVisitTargetId]);

  // Separate pending and completed visits
  const { pending, completed } = useMemo(() => {
    const pending = visitsWithPriority.filter(shouldSortVisit);
    const completed = visitsWithPriority.filter((v) => !shouldSortVisit(v));
    return { pending, completed };
  }, [visitsWithPriority]);

  // Stable client IDs for route optimization - only changes when clients change, not priorities
  const pendingClientIds = useMemo(() => {
    return pending
      .filter((v) => v.client.parkingLat != null && v.client.parkingLon != null)
      .map((v) => v.client.id);
  }, [pending]);

  // Use ref to track previous client IDs and avoid unnecessary re-sorting
  const prevClientIdsRef = useRef<string>('');
  const clientIdsKey = pendingClientIds.join(',');

  // Find last visited client (for fromLast mode)
  const lastVisitedClient = useMemo(() => {
    const visited = visitsWithPriority
      .filter((v) => v.visit?.status === 'VISITED' && v.visit?.visitedAt)
      .sort((a, b) => {
        const dateA = new Date(a.visit!.visitedAt!).getTime();
        const dateB = new Date(b.visit!.visitedAt!).getTime();
        return dateB - dateA;
      });
    return visited[0] ?? null;
  }, [visitsWithPriority]);

  // Fetch user's current location for fromMe mode
  const fetchUserLocation = useCallback(async (): Promise<{ lat: number; lon: number } | null> => {
    try {
      const status = await locationApi.getStatus();
      if (status.lastLocation) {
        return {
          lat: status.lastLocation.latitude,
          lon: status.lastLocation.longitude,
        };
      }
    } catch {
      // Silently fail - will fallback
    }
    return null;
  }, []);

  // Run sorting via backend - uses stable pendingClientIds to avoid re-triggering on priority changes
  const runSorting = useCallback(async (clientIds: number[]) => {
    if (clientIds.length === 0) {
      setError('Нет клиентов с координатами');
      return;
    }

    setIsCalculating(true);
    setError(null);

    try {
      const mode = getOptimizeMode(sortMode);
      let startClientId: number | undefined;
      let startPosition: { lat: number; lon: number } | undefined;

      // Prepare mode-specific parameters
      if (mode === 'fromMe') {
        const gpsLocation = await fetchUserLocation();
        if (gpsLocation) {
          startPosition = gpsLocation;
          setStartLocation(gpsLocation);
        } else {
          toast.info('GPS недоступен', { description: 'Используем последний визит' });
          // Fallback to fromLast
          startClientId = lastVisitedClient?.client.id;
          if (lastVisitedClient?.client.parkingLat && lastVisitedClient?.client.parkingLon) {
            setStartLocation({
              lat: lastVisitedClient.client.parkingLat,
              lon: lastVisitedClient.client.parkingLon,
            });
          }
        }
      } else if (mode === 'fromLast') {
        startClientId = lastVisitedClient?.client.id;
        if (!startClientId) {
          toast.info('Нет последнего визита', { description: 'Кратчайший маршрут' });
        }
        if (lastVisitedClient?.client.parkingLat && lastVisitedClient?.client.parkingLon) {
          setStartLocation({
            lat: lastVisitedClient.client.parkingLat,
            lon: lastVisitedClient.client.parkingLon,
          });
        }
      } else {
        setStartLocation(null);
      }

      // Call backend optimization
      const result = await optimizeRoute({
        clientIds,
        mode: startPosition ? 'fromMe' : (startClientId ? 'fromLast' : 'shortest'),
        startClientId,
        startPosition,
      });

      setSortedOrder(result.order);
    } catch (err) {
      console.error('Sorting error:', err);
      setError('Ошибка расчёта маршрута');
      toast.error('Ошибка расчёта маршрута', {
        description: 'Используется сортировка по имени',
      });
      setSortedOrder(null);
    } finally {
      setIsCalculating(false);
    }
  }, [
    sortMode,
    fetchUserLocation,
    lastVisitedClient,
  ]);

  // Run priority GPS sorting - uses backend API for priority-grouped optimization
  const runPriorityGpsSorting = useCallback(async (visits: SortedVisitItem[]) => {
    const visitsWithCoords = visits.filter(
      (v) => v.client.parkingLat != null && v.client.parkingLon != null
    );

    if (visitsWithCoords.length === 0) {
      setError('Нет клиентов с координатами');
      return;
    }

    setIsCalculating(true);
    setError(null);

    try {
      const mode = getOptimizeMode(sortMode);
      let startClientId: number | undefined;
      let startPosition: { lat: number; lon: number } | undefined;

      // Get start position (GPS or last visited client)
      if (mode === 'fromMe') {
        const gpsLocation = await fetchUserLocation();
        if (gpsLocation) {
          startPosition = gpsLocation;
          setStartLocation(gpsLocation);
        } else {
          toast.info('GPS недоступен', { description: 'Используем последний визит' });
          // Fallback to fromLast
          startClientId = lastVisitedClient?.client.id;
          if (lastVisitedClient?.client.parkingLat && lastVisitedClient?.client.parkingLon) {
            setStartLocation({
              lat: lastVisitedClient.client.parkingLat,
              lon: lastVisitedClient.client.parkingLon,
            });
          }
        }
      } else if (mode === 'fromLast') {
        startClientId = lastVisitedClient?.client.id;
        if (!startClientId) {
          toast.info('Нет последнего визита', { description: 'Кратчайший маршрут' });
        }
        if (lastVisitedClient?.client.parkingLat && lastVisitedClient?.client.parkingLon) {
          setStartLocation({
            lat: lastVisitedClient.client.parkingLat,
            lon: lastVisitedClient.client.parkingLon,
          });
        }
      } else {
        setStartLocation(null);
      }

      // Build client IDs and priorities arrays
      const clientIds = visitsWithCoords.map((v) => v.client.id);
      // Priority: lower value = higher priority (inverted PRIORITY_CODES)
      const priorities = visitsWithCoords.map((v) => -PRIORITY_CODES[v.priority]);

      // Call backend API
      const result = await optimizeRouteWithPriorities({
        clientIds,
        priorities,
        mode: startPosition ? 'fromMe' : (startClientId ? 'fromLast' : 'shortest'),
        startClientId,
        startPosition,
      });

      setSortedOrder(result.order);
    } catch (err) {
      console.error('Priority GPS sorting error:', err);
      setError('Ошибка расчёта маршрута');
      toast.error('Ошибка расчёта маршрута', {
        description: 'Используется сортировка по приоритету',
      });
      setSortedOrder(null);
    } finally {
      setIsCalculating(false);
    }
  }, [sortMode, fetchUserLocation, lastVisitedClient]);

  // Track previous modes to detect changes
  const prevPriorityModeRef = useRef<PriorityMode>(priorityMode);
  const prevSortModeRef = useRef<SortMode>(sortMode);

  // Run sorting when dependencies change
  // Only re-sort when actual client list changes (not on priority/status changes from optimistic updates)
  useEffect(() => {
    if (sortMode === 'name' || sortMode === 'class') {
      setSortedOrder(null);
      setStartLocation(null);
      prevClientIdsRef.current = '';
      prevSortModeRef.current = sortMode;
      return;
    }

    // Re-run if priority mode or sort mode changed
    const priorityModeChanged = prevPriorityModeRef.current !== priorityMode;
    const sortModeChanged = prevSortModeRef.current !== sortMode;
    prevPriorityModeRef.current = priorityMode;
    prevSortModeRef.current = sortMode;

    // Skip if nothing relevant changed
    if (clientIdsKey === prevClientIdsRef.current && !priorityModeChanged && !sortModeChanged) {
      return;
    }

    prevClientIdsRef.current = clientIdsKey;

    if (pendingClientIds.length > 0) {
      if (priorityMode === 'priorityGps') {
        // Use client-side optimization with priority groups
        runPriorityGpsSorting(pending);
      } else {
        // Use backend optimization (for 'none' and 'priority' modes)
        runSorting(pendingClientIds);
      }
    }
  }, [sortMode, clientIdsKey, pendingClientIds, runSorting, runPriorityGpsSorting, priorityMode, pending]);

  // Apply sorting - reorder pending visits by sortedOrder
  const sortedPending = useMemo(() => {

    if (sortMode === 'name') {
      // When usePriority is enabled, sort by priority first, then by name
      if (usePriority) {
        return [...pending].sort((a, b) => {
          const priorityDiff = getPriorityGroup(a.priority) - getPriorityGroup(b.priority);
          if (priorityDiff !== 0) return priorityDiff;
          return ruCollator(a.client.name, b.client.name);
        });
      }
      return sortByName(pending);
    }

    if (sortMode === 'class') {
      return sortByName(pending);
    }

    // Backend-optimized sorting
    if (!sortedOrder || sortedOrder.length === 0) {
      // Fallback: sort by priority if enabled, then by name
      if (usePriority) {
        return [...pending].sort((a, b) => {
          const priorityDiff = getPriorityGroup(a.priority) - getPriorityGroup(b.priority);
          if (priorityDiff !== 0) return priorityDiff;
          return ruCollator(a.client.name, b.client.name);
        });
      }
      return sortByName(pending);
    }

    // Build a map from clientId to visit item and route position
    const visitsByClientId = new Map<number, SortedVisitItem>();
    const routePosition = new Map<number, number>();
    for (const v of pending) {
      visitsByClientId.set(v.client.id, v);
    }
    sortedOrder.forEach((clientId, idx) => {
      routePosition.set(clientId, idx);
    });

    if (usePriority) {
      // Group by priority, then sort within each group by route position
      const grouped = new Map<number, SortedVisitItem[]>();
      for (const v of pending) {
        const group = getPriorityGroup(v.priority);
        if (!grouped.has(group)) {
          grouped.set(group, []);
        }
        grouped.get(group)!.push(v);
      }

      // Sort groups by priority (lower group number = higher priority)
      const sortedGroups = [...grouped.entries()].sort((a, b) => a[0] - b[0]);

      // Within each group, sort by route position (or name if no position)
      const result: SortedVisitItem[] = [];
      for (const [, items] of sortedGroups) {
        items.sort((a, b) => {
          const posA = routePosition.get(a.client.id) ?? Infinity;
          const posB = routePosition.get(b.client.id) ?? Infinity;
          if (posA !== posB) return posA - posB;
          return ruCollator(a.client.name, b.client.name);
        });
        result.push(...items);
      }
      return result;
    }

    // No priority grouping - just use route order
    const reordered: SortedVisitItem[] = [];
    for (const clientId of sortedOrder) {
      const visit = visitsByClientId.get(clientId);
      if (visit) {
        reordered.push(visit);
        visitsByClientId.delete(clientId);
      }
    }

    // Add any remaining clients (without coordinates) at the end
    const remaining = [...visitsByClientId.values()];
    remaining.sort((a, b) => ruCollator(a.client.name, b.client.name));

    return [...reordered, ...remaining];
  }, [sortMode, pending, sortedOrder, usePriority]);

  // Sort completed by visit date (most recent first)
  const sortedCompleted = useMemo(() => {
    return [...completed].sort((a, b) => {
      const dateA = a.visit?.visitedAt
        ? new Date(a.visit.visitedAt).getTime()
        : 0;
      const dateB = b.visit?.visitedAt
        ? new Date(b.visit.visitedAt).getTime()
        : 0;
      return dateB - dateA;
    });
  }, [completed]);

  return {
    sortedVisits: [...sortedPending, ...sortedCompleted],
    pendingVisits: sortedPending,
    completedVisits: sortedCompleted,
    isCalculating,
    startLocation,
    error,
  };
}

// Re-export for compatibility
export { type PriorityType };
